import { colors } from '@atlaskit/theme';
import { CurrencyISOCode } from '@atlassiansox/bxp-use-pricing';
import { FormattedPriceCalculationResponse } from '@atlassiansox/bxp-use-pricing';
import styled from '@emotion/styled';
import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { usePriceFinderStore } from '../../../store';
import { PricingExampleAnnual } from './pricing-example-annual';
import { PricingExampleMonthly } from './pricing-example-monthly';
import { deviceMediaQueries } from '../../../utils';

export type PricingExampleProps = {
  unitCounts: number[];
  priceCalc: FormattedPriceCalculationResponse;
  showFirstFlatRateTier: boolean;
  currencyISOCode: CurrencyISOCode;
};

export const PricingExample = ({
  unitCounts,
  priceCalc,
  showFirstFlatRateTier,
  currencyISOCode
}: PricingExampleProps) => {
  const [{ billingCycle }] = usePriceFinderStore();
  const [billingPeriod, setBillingPeriod] = useState(billingCycle);

  useEffect(() => {
    setBillingPeriod(billingCycle);
  }, [billingCycle]);

  return (
    <DesktopContainer>
      {billingPeriod === 'MONTHLY' && (
        <BillingDescription>
          <FormattedMessage
            id="pricingExampleMonthlyBillingDescription"
            defaultMessage="Monthly plans give you flexibility as your number of active users changes, so pay for seats as you use them. Remember that the number of seats may change the total price."
            description="Explanation of monthly pricing in pricing example."
          />
        </BillingDescription>
      )}

      {billingPeriod === 'ANNUAL' && (
        <BillingDescription>
          <FormattedMessage
            id="pricingExampleAnnualBillingDescription"
            defaultMessage="Annual plans give you flexibility and help you prepare for growth. Buy a group of seats at a flat rate now, then fill your empty seats through the year at no extra cost."
            description="Explanation of annual pricing in pricing example."
          />
        </BillingDescription>
      )}
      <Example>
        {billingCycle === 'MONTHLY' ? (
          <FormattedMessage
            id="monthlyExampleLabel"
            defaultMessage="Monthly billing example"
            description="Monthly billing example header label"
          />
        ) : (
          <FormattedMessage
            id="annualExampleLabel"
            defaultMessage="Annual billing example"
            description="Annual billing example header label"
          />
        )}
      </Example>
      <MainContainer>
        {billingPeriod === 'MONTHLY' && (
          <PricingExampleMonthly
            unitCounts={unitCounts}
            priceCalc={priceCalc}
            showFirstFlatRateTier={showFirstFlatRateTier}
          />
        )}

        {billingPeriod === 'ANNUAL' && (
          <PricingExampleAnnual
            currencyISOCode={currencyISOCode}
            unitCounts={unitCounts}
            priceCalc={priceCalc}
          />
        )}
      </MainContainer>
    </DesktopContainer>
  );
};

const BillingDescription = styled.div`
  ${deviceMediaQueries.desktop} {
    color: #000000;
    font-family: 'Charlie Text';
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
    margin: 0 auto;
    margin-bottom: 24px;
    margin-top: 5px;
    text-align: center;
    width: 468px;
  }
  ${deviceMediaQueries.mobile} {
    line-height: 18px;
    margin-bottom: 24px;
  }
  ${deviceMediaQueries.tablet} {
    grid-column: 1 / 4;
    margin-bottom: 24px;
  }
`;

const DesktopContainer = styled.div`
  ${deviceMediaQueries.desktop} {
    grid-column: 1 / 7;
    grid-row: 4;
    margin: 0 auto;
    padding-top: 24px;
    width: 568px;
  }
  ${deviceMediaQueries.mobile} {
    margin-top: 8px;
    padding-top: 20px;
  }
`;

const Example = styled.p`
  color: #000000;
  font-family: 'Charlie Text';
  font-size: 12px;
  font-style: italic;
  font-weight: 400;
  line-height: 18px;
  margin: 0;
`;

const MainContainer = styled.div`
  margin-top: 4px;
  ${deviceMediaQueries.mobile} {
    border-radius: 2px;
  }
  ${deviceMediaQueries.desktop} {
    background-color: white;
    border: 1px solid ${colors.N80};
    border-radius: 4px;
    margin-top: 4px;
    padding: 28px 48px 24px 32px;
  }
`;
